import classNames from 'classnames';
import { Team } from 'modules/teams/domain/interfaces/team';
import { TournamentBracket } from 'modules/championship/domain/interfaces/tournament-bracket';
import React, { useCallback } from 'react';
import { BracketTeamField } from '../bracket-team-field';
import { StyledBracketContainer, StyledRoundBrackets } from './round-brackets.styles';

export type BracketColor = 'white' | 'gray';

type BracketProps = {
  bracket: TournamentBracket;
  teams: Team[];
  className?: string;
  onBracketChanged?: (bracketId: number, teams: [number | null, number | null]) => void;
  color?: BracketColor;
};

const Bracket = ({ bracket, teams, className, onBracketChanged, color = 'white' }: BracketProps) => {
  const { roundId, id: bracketId } = bracket;
  const hasParent = !!bracket.parentBracketIds.length;
  const hasChild = !!bracket.childBracketId;
  const teamMatches = bracket.match?.teamMatches || [];
  const firstSelectedTeamId = teamMatches[0]?.team?.id || null;
  const secondSelectedTeamId = teamMatches[1]?.team?.id || null;

  // Use React state to track the current team IDs
  const [currentTeamIds, setCurrentTeamIds] = React.useState<[number | null, number | null]>([
    firstSelectedTeamId,
    secondSelectedTeamId,
  ]);

  // Update the state when the props change
  React.useEffect(() => {
    setCurrentTeamIds([firstSelectedTeamId, secondSelectedTeamId]);
  }, [firstSelectedTeamId, secondSelectedTeamId]);

  const handleBracketTeamChanged = useCallback(
    (index: number, teamId: number | null) => {
      if (!onBracketChanged) {
        return;
      }

      // Create a new array with the updated team ID
      const newTeamIds: [number | null, number | null] = [...currentTeamIds] as [number | null, number | null];
      newTeamIds[index] = teamId;

      // Update the local state
      setCurrentTeamIds(newTeamIds);

      // Call the parent callback with the updated team IDs
      onBracketChanged(bracketId, newTeamIds);
    },
    [bracketId, currentTeamIds, onBracketChanged],
  );

  return (
    <StyledBracketContainer
      className={classNames(className, {
        hasParent,
        hasChild,
      })}
    >
      <div className="teams-info">
        <div className={classNames('team-info-row', color)}>
          <BracketTeamField
            index={0}
            teams={teams}
            roundId={roundId}
            bracketId={bracketId}
            selectedTeamId={firstSelectedTeamId}
            onBracketTeamChange={handleBracketTeamChanged}
            onlyShow={!onBracketChanged}
            color={color}
          />
        </div>
        <div className={classNames('team-info-row', color)}>
          <BracketTeamField
            index={1}
            teams={teams}
            roundId={roundId}
            bracketId={bracketId}
            selectedTeamId={secondSelectedTeamId}
            onBracketTeamChange={handleBracketTeamChanged}
            onlyShow={!onBracketChanged}
            color={color}
          />
        </div>
      </div>
    </StyledBracketContainer>
  );
};

type BracketPairProps = {
  bracketPair: TournamentBracket[];
  teams: Team[];
  onBracketChanged?: (bracketId: number, teams: [number | null, number | null]) => void;
  color?: BracketColor;
};

const BracketPair = ({ bracketPair, teams, onBracketChanged, color }: BracketPairProps) => {
  const hasFirstBracket = Boolean(bracketPair[0]);
  const hasSecondBracket = Boolean(bracketPair[1]);

  return (
    <>
      {hasFirstBracket && (
        <Bracket className="bracket-match" bracket={bracketPair[0]} teams={teams} onBracketChanged={onBracketChanged} color={color} />
      )}

      {hasSecondBracket && (
        <Bracket className="bracket-match" bracket={bracketPair[1]} teams={teams} onBracketChanged={onBracketChanged} color={color} />
      )}
    </>
  );
};

type RoundBracketsProps = {
  bracketPairs: Array<TournamentBracket[]>;
  teams: Team[];
  className?: string;
  onBracketChanged?: (bracketId: number, teams: [number | null, number | null]) => void;
  color?: BracketColor;
};

export const RoundBrackets = ({ bracketPairs, teams, className, onBracketChanged, color }: RoundBracketsProps) => {
  return (
    <StyledRoundBrackets className={className}>
      {bracketPairs.map((pair) => (
        <BracketPair
          key={`${pair[0].id}-${pair[1]?.id}`}
          bracketPair={pair}
          teams={teams}
          onBracketChanged={onBracketChanged}
          color={color}
        />
      ))}
    </StyledRoundBrackets>
  );
};
